/**
 * Optimizely Web Experimentation event tracking utilities.
 *
 * Provides a safe, SSR-compatible way to push custom events into the
 * Optimizely Web Experimentation data layer (window['optimizely']).
 */
import {trackFxEvent} from './optimizely-fx-events'
import {trackFxProductPurchaseQuantity} from './optimizely-fx-commerce'
import {categoriesFromList} from './optimizely-fx-page-context'

/**
 * Category, Subcategory, length and single_size for the bag events, derived
 * from a SCAPI product or basket line (no backend hook sets these).
 * @param {Object} [product]
 * @returns {{category?: string, subcategory?: string, length?: boolean, singleSize?: boolean}}
 */
export const describeProductForOptimizely = (product) => {
    if (!product || typeof product !== 'object') return {}
    const attrs = Array.isArray(product.variationAttributes) ? product.variationAttributes : []
    const selected =
        product.variationValues && typeof product.variationValues === 'object'
            ? product.variationValues
            : null
    const byId = (id) => attrs.find((attr) => String(attr?.id || '').toLowerCase() === id)
    const {Category, Subcategory} = categoriesFromList(product.c_categories)

    // single_size = one size to pick (accessories), not one size in stock.
    // Unknown when the object carries neither the attributes nor a selection.
    let length
    let singleSize
    if (attrs.length) {
        const size = byId('size')
        length = !!byId('length')
        singleSize = !size || (size.values || []).length === 1
    } else if (selected) {
        length = 'length' in selected
        singleSize = !('size' in selected)
    }
    return {
        category: Category || product.c_primaryCategory || product.primaryCategoryId,
        subcategory: Subcategory,
        length,
        singleSize
    }
}

const stripNullish = (obj) => {
    if (!obj || typeof obj !== 'object') return undefined
    const cleaned = {}
    for (const [key, val] of Object.entries(obj)) {
        if (val !== undefined && val !== null) {
            cleaned[key] = val
        }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

/**
 * Push a custom event to the Optimizely Web Experimentation data layer.
 *
 * @param {Object} params
 * @param {string} params.eventName - Required Optimizely event name (e.g. "add_to_bag").
 * @param {Object} [params.tags] - Optional tags object (revenue, value).
 * @param {Object} [params.properties] - Optional properties object (source, Category, SKU, etc.).
 */
export const trackOptimizelyEvent = ({eventName, tags, properties}) => {
    if (typeof window === 'undefined') return

    try {
        window['optimizely'] = window['optimizely'] || []

        const payload = {
            type: 'event',
            eventName
        }

        const cleanTags = stripNullish(tags)
        if (cleanTags) {
            payload.tags = cleanTags
        }

        const cleanProps = stripNullish(properties)
        if (cleanProps) {
            payload.properties = cleanProps
        }

        window['optimizely'].push(payload)
    } catch (error) {
        console.error('[Optimizely Web Exp] Failed to track event:', eventName, error)
    }

    // Mirror into Feature Experimentation as a conversion event. Never throws.
    trackFxEvent(eventName, tags, properties)
}

/**
 * Track an "add_to_bag" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.subcategory] - Product subcategory.
 * @param {boolean} [params.length] - Product has a length variation attribute.
 * @param {boolean} [params.singleSize] - Product has one size (or none).
 * @param {string} [params.source] - UI surface that triggered the ATB (e.g. "quick-view").
 */
export const trackAddToBagEvent = ({
    product,
    sku,
    variationGroupId,
    value,
    category,
    subcategory,
    length,
    singleSize,
    source
} = {}) => {
    const derived = describeProductForOptimizely(product)
    trackOptimizelyEvent({
        eventName: 'add_to_bag',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category ?? derived.category,
            Subcategory: subcategory ?? derived.subcategory,
            length: length ?? derived.length,
            single_size: singleSize ?? derived.singleSize,
            source,
            URL: typeof window !== 'undefined' ? window.location.href : undefined
        }
    })
}

/**
 * Track an "remove_from_bag" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.subcategory] - Product subcategory.
 * @param {boolean} [params.length] - Product has a length variation attribute.
 * @param {boolean} [params.singleSize] - Product has one size (or none).
 * @param {string} [params.source] - UI surface that triggered the ATB (e.g. "quick-view").
 */
export const trackRemoveFromBagEvent = ({
    product,
    sku,
    variationGroupId,
    value,
    category,
    subcategory,
    length,
    singleSize,
    source
} = {}) => {
    const derived = describeProductForOptimizely(product)
    trackOptimizelyEvent({
        eventName: 'remove_from_bag',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category ?? derived.category,
            Subcategory: subcategory ?? derived.subcategory,
            length: length ?? derived.length,
            single_size: singleSize ?? derived.singleSize,
            source,
            URL: typeof window !== 'undefined' ? window.location.href : undefined
        }
    })
}

/**
 * Track an "add_to_wishlist" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.source] - UI surface that triggered the AWL (e.g. "pdp").
 */
export const trackAddToWishlistEvent = ({sku, variationGroupId, value, category, source} = {}) => {
    trackOptimizelyEvent({
        eventName: 'add_to_wishlist',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category,
            source,
            URL: typeof window !== 'undefined' ? window.location.href : undefined
        }
    })
}

/**
 * Track an "remove_from_wishlist" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.source] - UI surface that triggered the AWL (e.g. "pdp").
 */
export const trackRemoveFromWishlistEvent = ({
    sku,
    variationGroupId,
    value,
    category,
    source
} = {}) => {
    trackOptimizelyEvent({
        eventName: 'remove_from_wishlist',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category,
            source,
            URL: typeof window !== 'undefined' ? window.location.href : undefined
        }
    })
}

/**
 * Track a "product_purchase" event per purchased line (value = line total in
 * dollars) and its FX-only twin "product_purchase_quantity" (value = units).
 * Order revenue travels on the FX "purchase" event, not here.
 *
 * @param {Array<Object>} items from buildPurchasedItems (optimizely-fx-commerce)
 */
export const trackProductPurchasesEvent = (items = []) => {
    if (!Array.isArray(items)) return

    items.forEach((item) => {
        trackOptimizelyEvent({
            eventName: 'product_purchase',
            tags: {
                value: item.lineTotal
            },
            properties: {
                SKU: item.productId,
                Category: item.category,
                Subcategory: item.subcategory,
                Text: item.name,
                quantity: item.quantity,
                unit_price: item.unitPrice,
                line_total: item.lineTotal,
                order_no: item.orderNo,
                variation_group: item.variationGroup,
                add_location: item.addLocation,
                size: item.size,
                color: item.color,
                discounted: item.discounted
            }
        })
        trackFxProductPurchaseQuantity(item)
    })
}
